import React, { useState, useEffect, useCallback } from 'react';
import { getOCELMetadata, apiClient } from '../../../services/apiClient';
import { useOcelContext } from '../../../stores/OcelContext';

/**
 * Extract object attribute names and their declared OCEL 2.0 types from metadata.
 * Unions the data-derived attribute names (object_attribute_names) with the
 * attributes declared in the objectTypes schema, so schema-declared attributes
 * (e.g. timestamp-typed ones) always appear even without values in ocel.objects.
 */
const extractObjectAttributes = (metadata) => {
  const names = new Set(
    Array.isArray(metadata?.object_attribute_names)
      ? metadata.object_attribute_names
      : Object.keys(metadata?.object_attribute_names || {})
  );
  const types = {};
  for (const objectType of metadata?.object_types_schema || []) {
    for (const attr of objectType.attributes || []) {
      if (!attr?.name) continue;
      names.add(attr.name);
      if (attr.type && !types[attr.name]) types[attr.name] = attr.type;
    }
  }
  return { attributes: [...names].sort(), types };
};

/**
 * ObjectAttributeFilterControl - Control for object attribute filters
 *
 * Allows adding multiple attribute-value filter pairs with enable/disable toggles.
 * Reads available object attributes from the pre-loaded OcelContext metadata,
 * falling back to an API call when context metadata is unavailable.
 *
 * @param {Array} value - Current object attribute filters [{ attribute, value, enabled, mode }]
 * @param {Function} onChange - Callback when filters change
 * @param {string} activeFile - Current active file for fetching metadata
 * @param {boolean} disabled - Whether control is disabled
 * @param {boolean} showMode - Whether to render a per-row Include/Exclude selector. Off by
 *   default: only the local-filter path (via LocalFilterPanel) understands per-condition mode
 *   end-to-end (filterRequest.js, cache_warming.py, the backend engine); other callers of this
 *   component (e.g. AttributeSumLineChart's ReferenceLineSettings) send their conditions through
 *   a request-building path that doesn't consult mode, so the control stays exactly as before
 *   for them.
 */
const ObjectAttributeFilterControl = ({ value = [], onChange, activeFile, disabled = false, showMode = false }) => {
  const { metadata: contextMetadata } = useOcelContext() || {};
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [attributeTypes, setAttributeTypes] = useState({});
  const [attributeValues, setAttributeValues] = useState({}); // Cache of values per attribute
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadingValues, setLoadingValues] = useState({});

  const fetchAttributes = useCallback(async () => {
    if (!activeFile) return;

    // Prefer the pre-loaded OcelContext metadata (zero API calls)
    if (contextMetadata?.object_attribute_names && contextMetadata.file_type === activeFile) {
      const { attributes, types } = extractObjectAttributes(contextMetadata);
      setAvailableAttributes(attributes);
      setAttributeTypes(types);
      setLoadError(null);
      console.log(`✅ ObjectAttributeFilterControl - Loaded ${attributes.length} object attributes from OcelContext`);
      return;
    }

    setLoading(true);
    setLoadError(null);
    try {
      const metadata = await getOCELMetadata(activeFile);
      console.log('📊 ObjectAttributeFilterControl - Fetched metadata:', metadata);

      const { attributes, types } = extractObjectAttributes(metadata);
      setAvailableAttributes(attributes);
      setAttributeTypes(types);
      console.log(`✅ ObjectAttributeFilterControl - Loaded ${attributes.length} object attributes`);
    } catch (err) {
      console.error('❌ ObjectAttributeFilterControl - Failed to fetch attributes:', err);
      setAvailableAttributes([]);
      setAttributeTypes({});
      setLoadError(err.message || 'Failed to load object attributes');
    } finally {
      setLoading(false);
    }
  }, [activeFile, contextMetadata]);

  useEffect(() => {
    fetchAttributes();
  }, [fetchAttributes]);

  // Fetch possible values for a specific attribute
  const fetchAttributeValues = async (attribute) => {
    if (!activeFile || !attribute) return;
    if (attributeValues[attribute]) return; // Already cached

    setLoadingValues(prev => ({ ...prev, [attribute]: true }));
    try {
      const response = await apiClient.get(`/ocel/object-attributes/${encodeURIComponent(attribute)}/values`, {
        params: { file_type: activeFile }
      });

      const values = response.data;
      setAttributeValues(prev => ({ ...prev, [attribute]: values }));
      console.log(`✅ ObjectAttributeFilterControl - Loaded ${values.length} values for attribute "${attribute}"`);
    } catch (err) {
      console.error(`❌ ObjectAttributeFilterControl - Failed to fetch values for attribute "${attribute}":`, err);
      setAttributeValues(prev => ({ ...prev, [attribute]: [] }));
    } finally {
      setLoadingValues(prev => ({ ...prev, [attribute]: false }));
    }
  };

  const handleAddFilter = () => {
    if (disabled) return;

    const newFilter = { attribute: '', value: '', enabled: true, mode: 'include' };
    onChange([...value, newFilter]);
  };

  const handleRemoveFilter = (index) => {
    if (disabled) return;

    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
  };

  const handleFilterChange = (index, field, newValue) => {
    if (disabled) return;

    const newFilters = [...value];
    newFilters[index] = { ...newFilters[index], [field]: newValue };

    // If attribute changed, fetch its values and reset value field
    if (field === 'attribute' && newValue) {
      fetchAttributeValues(newValue);
      newFilters[index].value = '';
    }

    onChange(newFilters);
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Object Attribute Filters</label>
        <div className="text-sm text-gray-500 italic">Loading object attributes...</div>
      </div>
    );
  }

  if (loadError && availableAttributes.length === 0) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Object Attribute Filters</label>
        <div className="text-sm text-red-600">Failed to load object attributes: {loadError}</div>
        <button
          type="button"
          onClick={fetchAttributes}
          disabled={disabled}
          className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (availableAttributes.length === 0 && !loading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Object Attribute Filters</label>
        <div className="text-sm text-gray-500 italic">No object attributes available</div>
      </div>
    );
  }

  const activeCount = value.filter(f => f.enabled && f.attribute && f.value).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Object Attribute Filters
          {activeCount > 0 && (
            <span className="ml-2 text-xs text-gray-500">({activeCount} active)</span>
          )}
        </label>
      </div>

      <div className="space-y-2">
        {value.map((filter, index) => {
          const values = attributeValues[filter.attribute] || [];
          const isLoadingValues = loadingValues[filter.attribute];

          return (
            <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-md border border-gray-200">
              <div className="flex-1 space-y-2">
                {/* Attribute Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Attribute
                  </label>
                  <select
                    value={filter.attribute}
                    onChange={(e) => handleFilterChange(index, 'attribute', e.target.value)}
                    disabled={disabled}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select attribute...</option>
                    {availableAttributes.map((attr) => (
                      <option key={attr} value={attr}>
                        {attributeTypes[attr] ? `${attr} (${attributeTypes[attr]})` : attr}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Value Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Value
                  </label>
                  {isLoadingValues ? (
                    <div className="text-xs text-gray-500 italic py-1.5">Loading values...</div>
                  ) : values.length > 0 ? (
                    <select
                      value={filter.value}
                      onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                      disabled={disabled || !filter.attribute}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select value...</option>
                      {values.map((val) => (
                        <option key={val} value={val}>
                          {val}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={filter.value}
                      onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                      disabled={disabled || !filter.attribute}
                      placeholder="Enter value..."
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>

                {/* Mode (Include/Exclude) — per-condition, independent of the other rows */}
                {showMode && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Modus
                    </label>
                    <select
                      value={filter.mode || 'include'}
                      onChange={(e) => handleFilterChange(index, 'mode', e.target.value)}
                      disabled={disabled}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="include">Einschließen (=)</option>
                      <option value="exclude">Ausschließen (≠)</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col items-center gap-2 pt-6">
                <label className="flex items-center cursor-pointer" title={filter.enabled ? 'Enabled' : 'Disabled'}>
                  <input
                    type="checkbox"
                    checked={filter.enabled}
                    onChange={(e) => handleFilterChange(index, 'enabled', e.target.checked)}
                    disabled={disabled}
                    className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => handleRemoveFilter(index)}
                  disabled={disabled}
                  className="text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                  title="Remove filter"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Filter Button */}
      <button
        type="button"
        onClick={handleAddFilter}
        disabled={disabled}
        className="w-full px-3 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed transition-colors"
      >
        + Add Filter
      </button>

      {value.length === 0 && (
        <div className="text-xs text-gray-500 italic">
          No object attribute filters configured
        </div>
      )}
    </div>
  );
};

export default ObjectAttributeFilterControl;
